import React, { useContext, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import Icon from "../components/common/icons";
import annaAvatar from "../assets/Anna_1.png";
import { UserContext } from "../context/UserContext";
import { syncAppManifest } from "./appManifest";
import { detectPlatform, installVariant, isStandalone, openInBrowserLink } from "./platform";
import {
  canPromptInstall,
  isLaterDay,
  isGuidancePage,
  isMarkedInstalled,
  isSnoozed,
  markInstalled,
  OPEN_EVENT,
  promptInstall,
  snooze,
  subscribe,
} from "./installPrompt";
import "./pwa.css";

/**
 * The "Get the ANNA app" card. It opens from the "Get the app" buttons (the test
 * chat after the last test, "You're booked", the landing page, the account menu),
 * and by itself once per visit for signed-in people on a phone who come back on a
 * later day, at a calm moment (no chat, dialog, tour or menu open). What it shows depends on the phone:
 *   prompt        Chrome's own install popup is ready: one button
 *   ios-safari    numbered steps for Safari (the ☰ menu in the address bar on iOS 26, the Share button before)
 *   ios-other     steps for Chrome/Firefox/Edge on an iPhone
 *   android-menu  steps for the browser menu when Chrome's popup isn't ready
 *   in-app        opened inside Messenger/Facebook/…: open it in Safari/Chrome first
 * The words stay plain: button names the person will see are bold, nothing else is technical.
 *
 * On staff pages (/admin) the same card offers the staff app, ANNA Guidance, with its navy
 * icon and staff wording. It never opens by itself there; it opens from the setup page,
 * the "Finish setting up" card, and ?get-app=1 (where the phone QR sign-in lands).
 */

const PHONE_QUERY = "(max-width: 767.98px)";
// Anything on screen that the card must not cover
const BUSY_SELECTORS = ".modal.show, .chatbot, .gs-tour, .sh-sheet, .ap-root";
const FIRST_LOOK_DELAY_MS = 8000;
const CHECK_EVERY_MS = 1500;
const STEP_EVERY_MS = 2400;
const SNOOZE_DAYS = 14;
const DONE_SNOOZE_DAYS = 30;

let shownThisVisit = false;

// Development only: ?app-card=<name> opens the card the way that phone would see it.
const PREVIEW = process.env.NODE_ENV === "development" ? new URLSearchParams(window.location.search).get("app-card") : null;
const PREVIEW_PHONES = {
  prompt: { variant: "prompt", android: true, device: "phone", browserName: "Chrome" },
  "ios-safari": { variant: "ios-safari", ios: true, iosBrowser: "safari", safariVersion: 26, device: "iPhone", browserName: "Safari" },
  "ios-safari-old": { variant: "ios-safari", ios: true, iosBrowser: "safari", safariVersion: 17, device: "iPhone", browserName: "Safari" },
  "ios-other": { variant: "ios-other", ios: true, iosBrowser: "chrome", device: "iPhone", browserName: "Safari" },
  "android-menu": { variant: "android-menu", android: true, device: "phone", browserName: "Chrome" },
  "in-app": { variant: "in-app", android: true, inApp: "Messenger", device: "phone", browserName: "Chrome" },
  "in-app-ios": { variant: "in-app", ios: true, inApp: "Messenger", device: "iPhone", browserName: "Safari" },
};
const preview = PREVIEW_PHONES[PREVIEW] || null;

const usePhone = () => {
  const [phone, setPhone] = useState(() => window.matchMedia(PHONE_QUERY).matches);
  useEffect(() => {
    const query = window.matchMedia(PHONE_QUERY);
    const onChange = () => setPhone(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  return phone;
};

const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Safari 26 moved Share into the ☰ menu. In case its user agent doesn't say 26, also look for
// CSS that Safari only supports from 26 on (anchor positioning, scroll-driven animations).
const hasSafari26Css = () =>
  typeof CSS !== "undefined" &&
  typeof CSS.supports === "function" &&
  (CSS.supports("anchor-name: --anna") || CSS.supports("animation-timeline: scroll()"));

const isStudentPage = (pathname) => !pathname.startsWith("/admin") && !pathname.startsWith("/reset-password");

const isCalmMoment = () => document.visibilityState === "visible" && !document.querySelector(BUSY_SELECTORS);

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    const field = document.createElement("textarea");
    field.value = text;
    field.setAttribute("readonly", "");
    field.style.position = "fixed";
    field.style.opacity = "0";
    document.body.appendChild(field);
    field.select();
    let copied = false;
    try {
      copied = document.execCommand("copy");
    } catch (e) {
      copied = false;
    }
    field.remove();
    return copied;
  }
}

// What the person will see on their screen, drawn small next to each step
const Chip = ({ kind, children }) => <span className={`pwa-chip is-${kind}`} aria-hidden="true">{children}</span>;
const Switch = () => <span className="pwa-switch"><span /></span>;

// Safari 26's address bar: [<] [☰ web address ↻] [tabs]. Share sits in the ☰ menu, which
// looks like ANNA's own "menu" icon (three lines, the last one shorter).
const SafariMenuIcon = ({ size = 18 }) => <Icon name="menu" size={size} strokeWidth={2.2} className="pwa-inline-icon" />;

// On an iPhone the app keeps its own sign-in, apart from Safari's, so it asks once (N7)
const iosOpenStep = (staff) => ({
  text: <>Open {staff ? "Guidance" : "ANNA"} from your home screen and sign in</>,
  chip: <Chip kind="ios"><Icon name="sign-in" size={20} /></Chip>,
});

function stepsFor(variant, info, iosLayout, staff) {
  const IOS_OPEN_STEP = iosOpenStep(staff);
  if (variant === "ios-safari" && iosLayout === "menu") {
    return [
      { text: <>Tap <SafariMenuIcon /> on the left of the web address</>, chip: <Chip kind="ios"><SafariMenuIcon size={22} /></Chip> },
      { text: <>Tap <b>Share</b></>, chip: <Chip kind="ios"><Icon name="share" size={20} /></Chip> },
      { text: <>Scroll down and tap <b>Add to Home Screen</b></>, chip: <Chip kind="ios"><Icon name="plus-square" size={20} /></Chip> },
      { text: <>Keep <b>Open as Web App</b> on, then tap <b>Add</b></>, chip: <Chip kind="ios"><Switch /></Chip> },
      IOS_OPEN_STEP,
    ];
  }
  if (variant === "ios-safari" || variant === "ios-other") {
    const where = variant === "ios-other" ? "" : info.ipad ? " at the top of your screen" : " at the bottom of your screen";
    return [
      { text: <>Tap <b>Share</b>{where}</>, chip: <Chip kind="ios"><Icon name="share" size={20} /></Chip> },
      { text: <>Scroll down and tap <b>Add to Home Screen</b></>, chip: <Chip kind="ios"><Icon name="plus-square" size={20} /></Chip> },
      { text: <>Tap <b>Add</b></>, chip: <Chip kind="ios"><span className="pwa-chip-word">Add</span></Chip> },
      IOS_OPEN_STEP,
    ];
  }
  if (variant === "android-menu") {
    return [
      { text: <>Tap <b>⋮</b> at the top of your screen</>, chip: <Chip kind="android"><Icon name="more-vertical" size={22} /></Chip> },
      { text: <>Tap <b>Install app</b> or <b>Add to Home screen</b></>, chip: <Chip kind="android"><Icon name="plus-square" size={20} /></Chip> },
      { text: <>Tap <b>Install</b></>, chip: <Chip kind="android"><Icon name="download" size={20} /></Chip> },
    ];
  }
  return [];
}

export default function InstallAppPrompt() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const staff = isGuidancePage(pathname);
  const { isAuthenticated } = useContext(UserContext) || {};
  const phone = usePhone();
  const info = useMemo(() => (preview ? { ...detectPlatform(), mobile: true, ...preview } : detectPlatform()), []);
  const [canPrompt, setCanPrompt] = useState(canPromptInstall);
  const [open, setOpen] = useState(Boolean(preview));
  const [iosLayout, setIosLayout] = useState(() => ((info.safariVersion >= 26 || hasSafari26Css()) && !info.ipad ? "menu" : "share"));
  const [activeStep, setActiveStep] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => subscribe(() => setCanPrompt(canPromptInstall())), []);

  const variant = preview ? preview.variant : installVariant(info, canPrompt);
  const steps = stepsFor(variant, info, iosLayout, staff);

  // The home screen icon and name follow the side of the app this page is on
  useEffect(() => syncAppManifest(pathname), [pathname]);

  // A staff phone that just signed in from the setup QR lands on ?get-app=1: open the card once
  useEffect(() => {
    if (!staff || !new URLSearchParams(search).has("get-app")) return;
    const params = new URLSearchParams(search);
    params.delete("get-app");
    navigate({ pathname, search: params.toString() ? `?${params}` : "" }, { replace: true });
    if (!isStandalone()) setOpen(true);
  }, [staff, search, pathname, navigate]);

  // "Get the app" in the account menu opens the card at any time.
  useEffect(() => {
    const onOpen = () => {
      shownThisVisit = true;
      setOpen(true);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  // Open by itself once per visit for someone coming back on a later day, when nothing
  // else is on screen. On their first day the test chat and the booking page offer it instead.
  const offerBySelf =
    Boolean(isAuthenticated) &&
    info.mobile &&
    isStudentPage(pathname) &&
    variant !== null &&
    variant !== "android-menu" &&
    !isStandalone() &&
    !isMarkedInstalled() &&
    !isSnoozed() &&
    isLaterDay();

  useEffect(() => {
    if (!offerBySelf || open || shownThisVisit) return undefined;
    let calmChecks = 0;
    let timer = null;
    const check = () => {
      calmChecks = isCalmMoment() ? calmChecks + 1 : 0;
      if (calmChecks >= 2 && !shownThisVisit) {
        shownThisVisit = true;
        setOpen(true);
        return;
      }
      timer = setTimeout(check, CHECK_EVERY_MS);
    };
    timer = setTimeout(check, FIRST_LOOK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [offerBySelf, open]);

  // Light up one step at a time so it's clear where to start.
  useEffect(() => {
    setActiveStep(0);
    if (!open || steps.length < 2 || reducedMotion()) return undefined;
    const timer = setInterval(() => setActiveStep((step) => (step + 1) % steps.length), STEP_EVERY_MS);
    return () => clearInterval(timer);
  }, [open, steps.length, iosLayout]);

  const close = (days = SNOOZE_DAYS) => {
    snooze(days);
    setOpen(false);
  };

  const handleInstall = async () => {
    const outcome = await promptInstall();
    if (outcome === "accepted") {
      markInstalled();
      setOpen(false);
    } else {
      close();
    }
  };

  const handleCopy = async () => {
    if (await copyText(window.location.href)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 4000);
    }
  };

  if (!variant) return null;

  const inAppName = info.inApp === "another app" ? "another app" : info.inApp;
  const appName = staff ? "ANNA Guidance" : "the ANNA app";
  const title = variant === "in-app" ? `Open ANNA in ${info.browserName}` : staff ? "Get ANNA Guidance" : "Get the ANNA app";
  const oneTap = staff
    ? `Your schedule, sessions and requests, one tap away on your ${info.device}.`
    : `Open ANNA with one tap, right from your ${info.device}.`;
  const lead =
    variant === "in-app"
      ? `You opened ANNA inside ${inAppName}. To get ${appName}, open this page in ${info.browserName} first. You may need to log in again.`
      : variant === "prompt"
        ? oneTap
        : `${oneTap} Here's how to get it:`;
  // Points down at the button to tap in Safari's bar under the card: ☰ sits left of the web address
  const pointer = variant === "ios-safari" && !info.ipad ? (iosLayout === "menu" ? "is-menu" : "is-center") : null;

  return (
    <Modal
      show={open}
      onHide={() => close()}
      centered={!phone}
      className={staff ? "pwa-staff" : undefined}
      dialogClassName={phone ? "pwa-sheet" : "pwa-dialog"}
      contentClassName="pwa-card"
      aria-labelledby="pwa-title"
    >
      <Modal.Header closeButton>
        <Modal.Title id="pwa-title">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="pwa-hero">
          <img
            className={`pwa-hero-icon${staff ? " is-guidance" : ""}`}
            src={staff ? `${process.env.PUBLIC_URL || ""}/guidance/apple-touch-icon.png` : annaAvatar}
            alt=""
          />
          <p className="pwa-hero-text">{lead}</p>
        </div>

        {steps.length > 0 && (
          <ol className="pwa-steps">
            {steps.map((step, index) => (
              <li key={`${iosLayout}-${index}`} className={`pwa-step${index === activeStep ? " is-active" : ""}`}>
                <span className="pwa-step-num">{index + 1}</span>
                <span className="pwa-step-text">{step.text}</span>
                {step.chip}
              </li>
            ))}
          </ol>
        )}

        {variant === "ios-safari" && (
          <button
            type="button"
            className="pwa-link"
            onClick={() => setIosLayout((layout) => (layout === "menu" ? "share" : "menu"))}
          >
            {iosLayout === "menu" ? <>Don't see <SafariMenuIcon size={16} />? Show other steps</> : "Don't see Share? Show other steps"}
          </button>
        )}

        <div className="pwa-actions">
          {variant === "prompt" && (
            <>
              <button type="button" className="am-btn is-primary pwa-btn" onClick={handleInstall}>
                <Icon name="download" size={20} />
                Get the app
              </button>
              <button type="button" className="am-btn pwa-btn" onClick={() => close()}>
                Not now
              </button>
            </>
          )}

          {variant === "in-app" && (
            <>
              <a className="am-btn is-primary pwa-btn" href={openInBrowserLink(info, window.location.href)}>
                <Icon name="external-link" size={20} />
                Open in {info.browserName}
              </a>
              <button type="button" className="am-btn is-soft pwa-btn" onClick={handleCopy}>
                <Icon name={copied ? "check" : "copy"} size={20} />
                {copied ? "Link copied" : "Copy link"}
              </button>
              <p className="pwa-hint">
                {copied ? (
                  <>Now open <b>{info.browserName}</b> and paste the link there.</>
                ) : (
                  <>Nothing happened? Tap the <b>⋯</b> or <b>⋮</b> menu and choose to open it in <b>{info.browserName}</b>.</>
                )}
              </p>
            </>
          )}

          {(variant === "ios-safari" || variant === "ios-other" || variant === "android-menu") && (
            <button type="button" className="am-btn is-primary pwa-btn" onClick={() => close(DONE_SNOOZE_DAYS)}>
              Got it
            </button>
          )}
        </div>

        {pointer && (
          <div className={`pwa-pointer ${pointer}`} aria-hidden="true">
            <Icon name="arrow-down" size={22} strokeWidth={2.2} />
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}
